<template>
  <div class="clients-page">
    <!-- Header with Client Selector -->
    <div class="clients-page__header">
      <div class="clients-page__title-section">
        <h1 class="clients-page__title">{{ $gate.isAdmin() ? 'Clients' : 'Client' }}</h1>
        <div v-if="$gate.isAdmin()" class="clients-page__count">
          <span class="clients-page__count-separator">/</span>
          <span class="clients-page__count-text">{{ clients.filtered.length }} of {{ clients.all.length }}</span>
        </div>
      </div>
      
      <div v-if="$gate.isAdmin()" class="clients-page__actions">
        <themed-select
          v-model="selectedClientId"
          :options="clientOptions"
          placeholder="Select a Client"
          class="clients-page__selector"
        />
        
        <themed-button
          v-if="$gate.can('create', 'client')"
          @click="createClient"
          variant="default"
        >
          <theme-icon icon="plus-icon" type="icons" classes="w-4 h-4" />
          <span>New Client</span>
        </themed-button>
      </div>
    </div>

    <!-- Client Details -->
    <div class="clients-page__content">
      <themed-card v-if="loading" class="clients-page__loading">
        <div class="clients-page__loading-spinner">
          <theme-icon icon="loading" type="icons" classes="w-8 h-8 animate-spin" />
        </div>
      </themed-card>

      <themed-card v-else-if="selectedClientId && client" class="clients-page__details">
        <!-- Client Header -->
        <div class="clients-page__details-header">
          <div class="clients-page__details-info">
            <h2 class="clients-page__client-name">{{ client.name }}</h2>
          </div>
          <themed-button
            @click="openEditClient(client.id)"
            variant="default"
          >
            <theme-icon icon="edit-icon" type="icons" classes="w-4 h-4" />
            <span>Edit Client</span>
          </themed-button>
        </div>

        <!-- Stats Cards -->
        <div class="clients-page__stats">
          <div class="clients-page__stat-card clients-page__stat-card--users">
            <span class="clients-page__stat-value">{{ client.users.length }}</span>
            <span class="clients-page__stat-label">{{ client.users.length | pluralize('User') }}</span>
          </div>

          <div class="clients-page__stat-card clients-page__stat-card--subsidiaries">
            <span class="clients-page__stat-value">{{ client.subsidiaries.length }}</span>
            <span class="clients-page__stat-label">{{ client.subsidiaries.length | pluralize(['Subsidiary', 'Subsidiaries']) }}</span>
          </div>

          <div class="clients-page__stat-card clients-page__stat-card--sites">
            <span class="clients-page__stat-value">{{ client.sites.length }}</span>
            <span class="clients-page__stat-label">{{ client.sites.length | pluralize('Site') }}</span>
          </div>

          <div class="clients-page__stat-card clients-page__stat-card--projects">
            <span class="clients-page__stat-value">{{ client.projects.length }}</span>
            <span class="clients-page__stat-label">{{ client.projects.length | pluralize('Project') }}</span>
          </div>
        </div>

        <!-- Address and Map Section -->
        <div class="clients-page__location">
          <div class="clients-page__location-main">
            <themed-card class="clients-page__address-card">
              <div class="clients-page__address-content">
                <div class="clients-page__address-info">
                  <label class="clients-page__address-label">Address</label>
                  <div class="clients-page__address-text">
                    <p class="clients-page__address-line">{{ client.address }}</p>
                    <p class="clients-page__address-city">
                      {{ client.city }}, {{ client.state }} {{ client.postal_code }}
                    </p>
                    <p class="clients-page__address-country">{{ client.country.name }}</p>
                  </div>
                </div>
                <div v-if="client.latitude != null && client.longitude != null" class="clients-page__map">
                  <map-component
                    :latitude="client.latitude"
                    :longitude="client.longitude"
                    :zoom="6"
                  />
                </div>
              </div>
            </themed-card>
          </div>

          <div class="clients-page__location-sidebar">
            <upcoming-sites
              :sites="client.sites"
              :show-client-info="false"
            />

            <div class="clients-page__sidebar-spacer"></div>

            <active-projects
              :projects="client.projects"
              :show-client-info="false"
            />
          </div>
        </div>

        <!-- Tabs -->
        <themed-tabs
          v-model="selectedTab"
          :tabs="tabsConfig"
          class="clients-page__tabs"
        >
          <template v-slot="{ activeTab }">
            <!-- Users Tab -->
            <div v-if="activeTab === 'users'">
              <user-cards
                :user-data="client.users"
                :small-header="true"
              />
            </div>

            <!-- Subsidiaries Tab -->
            <div v-if="activeTab === 'subsidiaries'">
              <subsidiary-cards
                :subsidiary-data="client.subsidiaries"
                :for-client-id="client.id"
                :small-header="true"
                :show-column-visibility="false"
              />
            </div>

            <!-- Sites Tab -->
            <div v-if="activeTab === 'sites'">
              <div class="clients-page__tab-header clients-page__tab-header--sites">
                <div class="clients-page__tab-stats">
                  <div class="clients-page__tab-stat">
                    <p class="clients-page__tab-stat-value">{{ client.sites.length }}</p>
                    <p class="clients-page__tab-stat-label">{{ client.sites.length | pluralize('Site') }}</p>
                  </div>
                  <div class="clients-page__tab-stat">
                    <p class="clients-page__tab-stat-value">{{ formatShorthandCurrency(sitesTotalRemaining) }}</p>
                    <p class="clients-page__tab-stat-label">Total Remaining</p>
                  </div>
                  <div class="clients-page__tab-stat">
                    <p class="clients-page__tab-stat-value">{{ sitesTotalSqFt | number }}</p>
                    <p class="clients-page__tab-stat-label">Total Sq. Ft.</p>
                  </div>
                </div>
              </div>

              <site-cards
                :site-data="client.sites"
                :show-new-site-button="false"
                :show-client-info="false"
                :default-page-size="100"
                default-view-mode="list"
                :show-column-visibility="false"
              />
            </div>

            <!-- Projects Tab -->
            <div v-if="activeTab === 'projects'">
              <div class="clients-page__tab-header clients-page__tab-header--projects">
                <div class="clients-page__tab-stats">
                  <div class="clients-page__tab-stat">
                    <p class="clients-page__tab-stat-value">{{ client.projects.length }}</p>
                    <p class="clients-page__tab-stat-label">{{ client.projects.length | pluralize('Project') }}</p>
                  </div>
                  <div class="clients-page__tab-stat">
                    <p class="clients-page__tab-stat-value">{{ formatShorthandCurrency(projectsTotalRemaining) }}</p>
                    <p class="clients-page__tab-stat-label">Total Remaining</p>
                  </div>
                  <div class="clients-page__tab-stat">
                    <p class="clients-page__tab-stat-value">{{ projectsTotalSqFt | number }}</p>
                    <p class="clients-page__tab-stat-label">Total Sq. Ft.</p>
                  </div>
                </div>
              </div>

              <projects
                :project-data="client.projects"
                :show-toolbar="true"
                :show-title="false"
                :show-search="true"
                :show-view-toggle="true"
                :show-sort-filter="true"
                :show-filters="true"
                :show-display-count="true"
                :show-create-new="false"
                :show-completed-projects="true"
                :default-view-mode="'grid'"
                :per-page="10"
                :disable-preferences="true"
              />
            </div>
          </template>
        </themed-tabs>
      </themed-card>

      <themed-card v-else class="clients-page__empty">
        <table-empty
          :is-empty="true"
          icon="building-icon"
          message="Choose a client from the dropdown above to view details"
        />
      </themed-card>
    </div>

    <!-- Modals -->
    <client-modal
      :client="modalClient"
      :mode="modalMode"
      @saved="handleClientSaved"
    />
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { formatShorthandCurrency } from '../../../../utils/formatters';
import ThemedCard from '../ThemedCard.vue';
import ThemedButton from '../ThemedButton.vue';
import ThemedSelect from '../ThemedSelect.vue';
import ThemedTabs from '../ThemedTabs.vue';
import ThemeIcon from '../ThemeIcon.vue';
import TableEmpty from '../table/TableEmpty.vue';
import ClientModal from './ClientModal.vue';
import UserCards from '../users/UserCards.vue';
import SubsidiaryCards from '../subsidiaries/SubsidiaryCards.vue';
import SiteCards from '../sites/SiteCards.vue';
import Projects from '../projects/Projects.vue';
import UpcomingSites from '../sites/UpcomingSites.vue';
import ActiveProjects from '../projects/ActiveProjects.vue';
import MapComponent from '../../../maps/MapComponent.vue';

export default {
  name: 'ThemedClients',

  components: {
    ThemedCard,
    ThemedButton,
    ThemedSelect,
    ThemedTabs,
    ThemeIcon,
    TableEmpty,
    ClientModal,
    UserCards,
    SubsidiaryCards,
    SiteCards,
    Projects,
    UpcomingSites,
    ActiveProjects,
    MapComponent
  },

  props: {
    clientData: {
      type: Array,
      required: true
    },

    clientId: {
      type: Number,
      required: false
    },

    openTab: {
      type: String,
      default: 'users'
    }
  },

  data() {
    return {
      selectedClientId: this.clientId || '',
      selectedTab: this.openTab,
      loading: false,
      countries: {},
      states: {},
      modalMode: 'create',
      modalClient: null
    };
  },

  computed: {
    ...mapState(['clients']),
    ...mapState(['sites']),

    clientOptions() {
      return this.clients.filtered.map(client => ({
        value: client.id,
        label: client.name
      }));
    },

    tabsConfig() {
      return [
        {
          key: 'users',
          value: 'users',
          label: 'Users',
          count: this.client?.users.length || 0,
          showCount: true
        },
        {
          key: 'subsidiaries',
          value: 'subsidiaries',
          label: 'Subsidiaries',
          count: this.client?.subsidiaries.length || 0,
          showCount: true
        },
        {
          key: 'sites',
          value: 'sites',
          label: 'Sites',
          count: this.client?.sites.length || 0,
          showCount: true
        },
        {
          key: 'projects',
          value: 'projects',
          label: 'Projects',
          count: this.client?.projects.length || 0,
          showCount: true
        }
      ];
    },

    client() {
      return this.clients.selectedClient;
    },

    // remaining_lease_cost is stored in cents; convert to dollars for display.
    projectsTotalRemaining() {
      return _.sumBy(this.client.projects, 'remaining_lease_cost') / 100;
    },

    projectsTotalSqFt() {
      return _.sumBy(this.client.projects, 'required_sq_ft');
    },

    sitesTotalRemaining() {
      return _.sumBy(this.client?.sites || [], 'remaining_lease_cost') / 100;
    },

    sitesTotalSqFt() {
      return _.sumBy(this.client?.sites || [], 'size');
    }
  },

  watch: {
    selectedClientId() {
      this.getClient();
    },

    selectedTab() {
      this.$eventHub.$emit('convert-currency');
    }
  },

  created() {
    this.$eventHub.$on('reloadClient', () => {
      this.getClient();
    });

    axios.get('/api/address-info').then((response) => {
      this.countries = response.data.countries;
      this.states = response.data.states;
    }).catch((error) => {
      console.log(error);
    });

    this.clients.all = this.clientData;
    this.clients.filtered = this.clientData;

    if (this.selectedClientId) {
      this.getClient();
    }
  },

  methods: {
    formatShorthandCurrency,
    
    getClient() {
      this.loading = true;

      axios.get('/api/clients/' + this.selectedClientId).then((response) => {
        this.clients.selectedClient = response.data;
      }).catch((error) => {
        this.$notify({
          type: 'error',
          title: 'Error',
          text: 'Error Pulling Client'
        });
      }).finally(() => {
        this.loading = false;
      });
    },

    openEditClient(clientId) {
      this.modalMode = 'edit';
      this.modalClient = this.clients.selectedClient;
      this.$modal.show('client-modal');
    },

    createClient() {
      this.modalMode = 'create';
      this.modalClient = null;
      this.$modal.show('client-modal');
    },

    handleClientSaved(clientData) {
      if (this.modalMode === 'create') {
        // Add new client to list
        this.clients.filtered.unshift(clientData);
        this.clients.all.unshift(clientData);
        this.selectedClientId = clientData.id;
      } else {
        // Update existing client in list
        this.clients.filtered = this.clients.filtered.map(client => 
          client.id === clientData.id ? clientData : client
        );
        this.clients.all = this.clients.all.map(client => 
          client.id === clientData.id ? clientData : client
        );
        this.clients.selectedClient = clientData;
      }
      
      // Refresh client data
      this.getClient();
    },

    clientUpdated(clientData) {
      this.clients.filtered = _.remove(this.clients.filtered, (client) => {
        return client.id != clientData.id;
      });
      this.clients.filtered.unshift(clientData);
      this.clients.selectedClient = clientData;
    }
  }
};
</script>
